import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Sparkles, ArrowUpRight, Check, RotateCcw,
  ShoppingCart, Code2, Server, Mail, ShieldCheck, Plug, Megaphone,
} from 'lucide-react';

const NAVY      = '#2563EB';
const NAVY_SOFT = '#3B82F6';
const GOLD      = '#2563EB';
const GOLD_SOFT = '#3B82F6';
const GOLD_PALE = '#EFF6FF';

const intents = [
  { id: 'sell',       label: 'Sell online',                     pillars: ['shopify', 'integrations'] },
  { id: 'mobile',     label: 'Build a mobile app',              pillars: ['software'] },
  { id: 'web',        label: 'Build a web platform',            pillars: ['software'] },
  { id: 'email',      label: 'Set up business email',           pillars: ['email'] },
  { id: 'migrate',    label: 'Migrate from another platform',   pillars: ['shopify', 'email'] },
  { id: 'secure',     label: 'Secure my business',              pillars: ['cyber', 'email'] },
  { id: 'network',    label: 'Set up office network / Wi-Fi',   pillars: ['it'] },
  { id: 'cctv',       label: 'Install CCTV / access control',   pillars: ['it'] },
  { id: 'voip',       label: 'Set up phone system (VoIP)',      pillars: ['it'] },
  { id: 'zoho',       label: 'Implement Zoho / CRM',            pillars: ['integrations'] },
  { id: 'whatsapp',   label: 'Run WhatsApp marketing',          pillars: ['integrations'] },
  { id: 'cloud',      label: 'Move to the cloud',               pillars: ['software', 'it'] },
  { id: 'ai',         label: 'Add AI / automation',             pillars: ['software', 'integrations'] },
  { id: 'integrate',  label: 'Connect my systems together',     pillars: ['integrations', 'software'] },
  { id: 'brand',      label: 'Build / refresh my brand',        pillars: ['marketing'] },
  { id: 'social',     label: 'Manage my social media',          pillars: ['marketing'] },
  { id: 'ads',        label: 'Run paid ads (Meta / Google)',    pillars: ['marketing'] },
  { id: 'seo',        label: 'Rank higher on Google (SEO)',     pillars: ['marketing'] },
  { id: 'grow',       label: 'Grow my online sales',            pillars: ['marketing', 'shopify'] },
];

const pillarMap = {
  shopify:      { id: 'shopify',      icon: ShoppingCart, title: 'Shopify & eCommerce',         href: '/services/shopify',         tagline: 'Commerce, end-to-end' },
  software:     { id: 'software',     icon: Code2,        title: 'Software & Mobile',           href: '/services/software',        tagline: 'Custom builds, native to web' },
  it:           { id: 'it',           icon: Server,       title: 'IT Infrastructure',           href: '/services/it-solutions',    tagline: 'Networks, telephony & on-prem' },
  email:        { id: 'email',        icon: Mail,         title: 'Email & Workplace',           href: '/services/email-workplace', tagline: 'Microsoft 365 · Google Workspace' },
  cyber:        { id: 'cyber',        icon: ShieldCheck,  title: 'Cybersecurity',               href: '/services/networking',      tagline: 'Secure by design' },
  integrations: { id: 'integrations', icon: Plug,         title: 'Integrations & Partnerships', href: '/services/integrations',    tagline: 'Zoho · Microsoft · Interakt' },
  marketing:    { id: 'marketing',    icon: Megaphone,    title: 'Marketing & Creative',        href: '/services/marketing',       tagline: 'Branding · Social · Ads · SEO' },
};

export const ServiceFinderSection = () => {
  const [selected, setSelected] = useState([]);

  const toggle = (id) =>
    setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const reset = () => setSelected([]);

  const recommended = useMemo(() => {
    if (selected.length === 0) return [];
    const scores = {};
    let max = 0;
    selected.forEach((iid) => {
      const intent = intents.find((x) => x.id === iid);
      if (!intent) return;
      intent.pillars.forEach((pid) => {
        scores[pid] = (scores[pid] || 0) + 1;
        if (scores[pid] > max) max = scores[pid];
      });
    });
    return Object.entries(scores)
      .sort((a, b) => b[1] - a[1])
      .map(([pid, score]) => ({
        pillar: pillarMap[pid],
        score,
        match: Math.round((score / max) * 100),
      }))
      .filter((x) => x.pillar);
  }, [selected]);

  return (
    <section
      id="service-finder"
      className="section-padding bg-white border-t border-[#E2E8F0] relative overflow-hidden"
    >
      {/* Soft ambient blobs */}
      <div className="absolute top-0 -left-32 w-[500px] h-[500px] rounded-full bg-[#2563EB]/[0.04] blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 -right-32 w-[500px] h-[500px] rounded-full bg-[#3B82F6]/[0.04] blur-[120px] pointer-events-none" />

      <div className="container-custom relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-14"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="section-label">Service Finder</span>
            <span className="w-6 h-px bg-[#2563EB]/30" />
            <span className="font-mono text-[0.55rem] tracking-widest text-[#9CA3AF] uppercase flex items-center gap-1.5">
              <Sparkles size={10} />
              Interactive
            </span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2
              className="font-display font-bold text-[#0F172A]"
              style={{ fontSize: 'clamp(2rem, 4.4vw, 3.75rem)' }}
            >
              Not sure where to start?<br />
              <span className="italic text-gold">Tell us what you need.</span>
            </h2>
            <p className="text-[#374151] max-w-sm leading-relaxed text-sm lg:text-right">
              Pick what applies — we'll instantly match you with the right practice areas.
            </p>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-[1fr_460px] gap-8 items-start">
          {/* LEFT — Intent picker */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-[#F6F9FC] border border-[#E2E8F0] p-7 lg:p-9 relative"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <span className="font-mono text-[0.6rem] tracking-[0.22em] uppercase text-[#374151] font-medium">
                  I need help with…
                </span>
                <span className="font-mono text-[0.55rem] tracking-widest text-[#9CA3AF]">
                  ({selected.length} selected)
                </span>
              </div>
              {selected.length > 0 && (
                <button
                  onClick={reset}
                  className="flex items-center gap-1.5 text-xs text-[#374151] hover:text-[#0F172A] font-medium transition-colors"
                >
                  <RotateCcw size={11} />
                  Reset
                </button>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              {intents.map((intent) => {
                const isOn = selected.includes(intent.id);
                return (
                  <motion.button
                    key={intent.id}
                    onClick={() => toggle(intent.id)}
                    whileTap={{ scale: 0.97 }}
                    className={`group relative inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border transition-all duration-200 ${
                      isOn
                        ? 'bg-[#2563EB] text-white border-[#2563EB] shadow-sm'
                        : 'bg-white text-[#374151] border-[#E2E8F0] hover:border-[#2563EB]/50 hover:text-[#2563EB]'
                    }`}
                  >
                    <motion.span
                      animate={{ width: isOn ? 14 : 0, opacity: isOn ? 1 : 0 }}
                      transition={{ duration: 0.2 }}
                      className="flex items-center overflow-hidden"
                    >
                      <Check size={13} />
                    </motion.span>
                    {intent.label}
                  </motion.button>
                );
              })}
            </div>

            <div className="mt-8 pt-6 border-t border-[#E2E8F0]">
              <p className="text-xs text-[#374151] leading-relaxed">
                <span className="font-mono text-[0.58rem] tracking-widest text-[#9CA3AF] uppercase block mb-2">
                  Tip
                </span>
                Select multiple — most projects need more than one practice. Your matches update live as you choose.
              </p>
            </div>
          </motion.div>

          {/* RIGHT — Recommendations panel */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white border border-[#E2E8F0] sticky top-24 shadow-[0_4px_24px_rgba(11,61,92,0.05)]"
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between bg-[#FAFAF7]">
              <span className="font-mono text-[0.6rem] tracking-[0.22em] uppercase text-[#374151] font-medium">
                Your matches
              </span>
              <span className="flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${recommended.length > 0 ? 'bg-[#2563EB] pulse-dot' : 'bg-[#E2E8F0]'}`} />
                <span className="font-mono text-[0.55rem] tracking-widest font-semibold" style={{ color: recommended.length > 0 ? '#1E3A8A' : '#9CA3AF' }}>
                  {recommended.length > 0 ? `${recommended.length} FOUND` : 'IDLE'}
                </span>
              </span>
            </div>

            {/* Empty state */}
            <AnimatePresence mode="wait">
              {recommended.length === 0 ? (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="px-6 py-16 text-center"
                >
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center bg-[#EFF6FF] border border-[#2563EB]/20">
                    <Sparkles size={18} className="text-[#2563EB]" />
                  </div>
                  <p className="text-sm text-[#374151] leading-relaxed mb-1 font-medium">
                    Pick what you need
                  </p>
                  <p className="text-xs text-[#9CA3AF] leading-relaxed">
                    Recommendations will appear here in real time.
                  </p>
                </motion.div>
              ) : (
                <motion.div key="results">
                  {recommended.map(({ pillar, match }, i) => {
                    const Icon = pillar.icon;
                    return (
                      <motion.div
                        key={pillar.id}
                        layout
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.35, delay: i * 0.06 }}
                      >
                        <Link
                          to={pillar.href}
                          className="group block px-6 py-5 border-b border-[#E2E8F0] last:border-0 hover:bg-[#FAFAF7] transition-colors duration-300 relative overflow-hidden"
                        >
                          {/* Left accent rail (gold) */}
                          <div
                            className="absolute left-0 top-0 bottom-0 w-[2px] origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-300"
                            style={{ background: GOLD }}
                          />

                          {/* Best match badge for top result */}
                          {i === 0 && (
                            <span
                              className="absolute top-4 right-4 font-mono text-[0.5rem] tracking-widest uppercase font-semibold px-2 py-0.5"
                              style={{ background: NAVY, color: GOLD_SOFT }}
                            >
                              Best Match
                            </span>
                          )}

                          <div className="flex items-start gap-4">
                            <div
                              className="w-10 h-10 flex items-center justify-center border border-[#E2E8F0] shrink-0 group-hover:border-[#2563EB] group-hover:bg-[#2563EB] transition-all duration-300"
                              style={{ background: '#F6F9FC' }}
                            >
                              <Icon size={18} className="text-[#2563EB] group-hover:text-[#3B82F6] transition-colors duration-300" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-display text-base font-bold text-[#0F172A] mb-0.5">
                                {pillar.title}
                              </h4>
                              <p className="font-mono text-[0.55rem] tracking-widest uppercase mb-3" style={{ color: NAVY_SOFT }}>
                                {pillar.tagline}
                              </p>

                              {/* Match strength bar */}
                              <div className="flex items-center gap-2.5">
                                <div className="flex-1 h-[3px] bg-[#F1F0ED] overflow-hidden">
                                  <motion.div
                                    initial={{ width: 0 }}
                                    animate={{ width: `${match}%` }}
                                    transition={{ duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                                    className="h-full"
                                    style={{ background: `linear-gradient(90deg, ${NAVY_SOFT}, ${NAVY})` }}
                                  />
                                </div>
                                <span className="font-mono text-[0.55rem] tracking-wider font-semibold tabular-nums shrink-0" style={{ color: NAVY }}>
                                  {match}% match
                                </span>
                              </div>
                            </div>
                            <ArrowUpRight
                              size={15}
                              className="text-[#9CA3AF] group-hover:text-[#2563EB] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all shrink-0 mt-3"
                            />
                          </div>
                        </Link>
                      </motion.div>
                    );
                  })}

                  {/* Footer CTA */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className="px-6 py-4 bg-[#2563EB]"
                  >
                    <Link
                      to="/book"
                      className="group flex items-center justify-between text-sm font-semibold text-white hover:text-[#3B82F6] transition-colors"
                    >
                      <span>Book a free consultation</span>
                      <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </Link>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
